import { useState, useMemo, useEffect } from 'react'
import { Pencil, Plus, ShieldCheck, UserMinus, Users, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { PageShell } from '@/components/common/PageShell'
import {
  WorkspaceToolbar,
  WorkspaceToolbarSearch,
} from '@/components/common/WorkspaceToolbar'
import { Badge, Button, SegmentedControl } from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { EmptyState } from '@/components/common/EmptyState'
import { PermissionGate } from '@/components/common/PermissionGate'
import {
  TableRowAction,
  TableRowActions,
} from '@/components/common/TableRowActions'
import {
  DataTable,
  DataTableCard,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/components/common/DataTable'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { usePermissions } from '@/hooks/usePermissions'
import { COMPANY_MEMBERS_MODULE } from '@/config/permissions'
import { getCompanyColor } from '@/features/shifts/utils/companyColor'
import {
  useWorkspaceCompanyFilter,
  WorkspaceCompanyChips,
} from '@/features/companies/components/WorkspaceCompanyFilter'
import {
  useMembersWorkspace,
  useRemoveCompanyMember,
} from '@/features/companies/hooks/useCompanies'
import { formatCurrency } from '@/lib/formatters'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import type { CompanyMember } from '@/types/company.types'
import { AddCompanyMemberModal } from './AddCompanyMemberModal'
import { EditCompanyMemberModal } from './EditCompanyMemberModal'
import {
  RemoveCompanyMemberModal,
  type RemoveCompanyMemberChoice,
} from './RemoveCompanyMemberModal'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const
type StatusFilter = 'all' | 'active' | 'inactive'
type CompensationFilter = 'all' | 'salaried' | 'hourly'

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const

const COMPENSATION_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'salaried', label: 'Salaried' },
  { value: 'hourly', label: 'Hourly' },
] as const

type MemberUser = {
  _id: string
  email?: string
  firstName?: string
  lastName?: string
}

function getMemberUserId(member: CompanyMember): string {
  return typeof member.userId === 'string'
    ? member.userId
    : (member.userId as MemberUser)._id
}

function getMemberDisplayName(member: CompanyMember): string {
  if (typeof member.userId === 'string') return member.userId
  const u = member.userId as MemberUser
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ')
  return name || u.email || u._id
}

/** Returned only when it adds something the name doesn't already say. */
function getMemberEmail(member: CompanyMember): string | null {
  if (typeof member.userId === 'string') return null
  const email = (member.userId as MemberUser).email?.trim()
  if (!email) return null
  return email === getMemberDisplayName(member) ? null : email
}

function getMemberCompanyId(member: CompanyMember): string {
  return typeof member.companyId === 'string'
    ? member.companyId
    : (member.companyId as { _id: string })._id
}

function getMemberCompensationType(
  member: CompanyMember
): 'hourly' | 'salaried' | null {
  const compensationType = member.effectiveCompensation?.compensationType
  return compensationType === 'hourly' || compensationType === 'salaried'
    ? compensationType
    : null
}

function getCompensationSourceLabel(member: CompanyMember): string | null {
  if (
    member.hasCompensationOverride ||
    member.effectiveCompensation?.source === 'company_override'
  ) {
    return 'Company override'
  }
  if (member.effectiveCompensation?.source === 'user_compensation') {
    return 'Inherited'
  }
  return null
}

/**
 * The headline figure for the Compensation column: the money, not the category.
 * Returns `null` when no rate is recorded so the cell can say so plainly rather
 * than printing a misleading zero.
 */
function getMemberRateLabel(
  member: CompanyMember,
  fallbackCurrency: string
): string | null {
  const compensation = member.effectiveCompensation
  const type = getMemberCompensationType(member)
  if (!compensation || !type) return null
  const currency = compensation.currency?.trim() || fallbackCurrency
  const amount = type === 'hourly' ? compensation.hourlyRate : compensation.baseSalary
  if (amount === undefined || amount === null || Number.isNaN(amount)) return null
  return `${formatCurrency(amount, currency)} / ${type === 'hourly' ? 'hr' : 'mo'}`
}

export function WorkspaceMembersPage(): React.ReactElement {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [compensationFilter, setCompensationFilter] =
    useState<CompensationFilter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const filter = useWorkspaceCompanyFilter({ onChange: () => setPage(1) })
  const { currency: orgCurrency } = useOrgCurrency()

  useEffect(() => {
    const trimmed = searchInput.trim()
    if (trimmed === search) return undefined
    const timeout = window.setTimeout(() => {
      setSearch(trimmed)
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timeout)
  }, [searchInput, search])

  const query = useMemo(
    () => ({
      page,
      limit,
      companyIds: filter.companyIds,
      status: statusFilter === 'all' ? undefined : statusFilter,
      compensationType:
        compensationFilter === 'all' ? undefined : compensationFilter,
      search: search || undefined,
    }),
    [page, limit, filter.companyIds, statusFilter, compensationFilter, search]
  )
  const { data, isLoading, error, refetch } = useMembersWorkspace(query)

  const members = useMemo(() => data?.data ?? [], [data?.data])
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const { can } = usePermissions()
  const canUpdateMember = can(COMPANY_MEMBERS_MODULE, 'update')
  const canRemoveMember = can(COMPANY_MEMBERS_MODULE, 'remove')
  // Pay figures stay behind the same gate that lets someone open the edit
  // modal, where the rate has always been visible. Read-only viewers keep the
  // compensation column, minus the amount.
  const canSeeRates = canUpdateMember
  const hasActiveFilters =
    filter.selectedCompanyIds.size > 0 ||
    statusFilter !== 'all' ||
    compensationFilter !== 'all' ||
    searchInput.trim() !== ''

  const [addModalOpen, setAddModalOpen] = useState(false)
  const [memberToEdit, setMemberToEdit] = useState<CompanyMember | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<CompanyMember | null>(null)
  const removeMember = useRemoveCompanyMember()

  const companyById = useMemo(() => {
    const map = new Map<string, { name: string; color?: string }>()
    for (const company of filter.companies) {
      map.set(company._id, { name: company.name, color: company.color })
    }
    return map
  }, [filter.companies])

  // "Add member" needs exactly one company in scope. The simplest UX is to
  // require the user to pick a single chip first; this avoids forking the
  // existing AddCompanyMemberModal to also pick a company internally.
  const addCompanyId =
    filter.selectedCompanyIds.size === 1
      ? Array.from(filter.selectedCompanyIds)[0]
      : null

  const existingMembersForAdd = useMemo(
    () =>
      addCompanyId
        ? members.filter((m) => getMemberCompanyId(m) === addCompanyId)
        : [],
    [members, addCompanyId]
  )

  const handleConfirmRemove = (choice: RemoveCompanyMemberChoice): void => {
    if (memberToRemove) {
      removeMember.mutate(
        {
          companyId: getMemberCompanyId(memberToRemove),
          userId: getMemberUserId(memberToRemove),
          ...choice,
        },
        { onSuccess: () => setMemberToRemove(null) }
      )
    }
  }

  const handleResetFilters = (): void => {
    filter.reset()
    setStatusFilter('all')
    setCompensationFilter('all')
    setSearchInput('')
    setSearch('')
    setPage(1)
  }

  /**
   * Row click is a pointer convenience only — the Edit action button carries the
   * same behaviour for keyboard and assistive tech, so the row needs no tab stop.
   * Dragging to select text must not count as a click.
   */
  const handleRowActivate = (member: CompanyMember): void => {
    if (!canUpdateMember) return
    if (window.getSelection()?.toString()) return
    setMemberToEdit(member)
  }

  /**
   * Everything a row shows, derived once so the table and the mobile card list
   * can never drift apart.
   */
  const describeMember = (member: CompanyMember) => {
    const companyId = getMemberCompanyId(member)
    const displayName = getMemberDisplayName(member)
    const compensationType = getMemberCompensationType(member)
    return {
      key: `${companyId}:${getMemberUserId(member)}`,
      displayName,
      email: getMemberEmail(member),
      company: companyById.get(companyId),
      companyColor: getCompanyColor(companyId, companyById.get(companyId)?.color),
      rateLabel:
        canSeeRates && !member.payRatesHidden ? getMemberRateLabel(member, orgCurrency) : null,
      typeLabel: compensationType
        ? compensationType === 'hourly'
          ? 'Hourly'
          : 'Salaried'
        : 'No compensation set',
      sourceLabel: getCompensationSourceLabel(member),
      isActive: member.status === 'active',
      isManager: member.role === 'manager',
    }
  }

  const renderCompany = (
    row: ReturnType<typeof describeMember>
  ): React.ReactElement => (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: row.companyColor.dot }}
      />
      <span className="truncate text-gray-700 dark:text-gray-300">
        {row.company?.name ?? '—'}
      </span>
    </span>
  )

  /** Manager carries a mark; "Member" is the default and stays quiet. */
  const renderRole = (row: ReturnType<typeof describeMember>): React.ReactElement =>
    row.isManager ? (
      <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100">
        <ShieldCheck
          className="h-3.5 w-3.5 text-primary dark:text-sky-300"
          aria-hidden
        />
        Manager
      </span>
    ) : (
      <span className="text-gray-500 dark:text-gray-400">Member</span>
    )

  const renderCompensation = (
    row: ReturnType<typeof describeMember>
  ): React.ReactElement => (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          'font-medium text-gray-900 dark:text-gray-100',
          row.rateLabel && 'tabular-nums'
        )}
      >
        {row.rateLabel ?? row.typeLabel}
      </span>
      {row.rateLabel || row.sourceLabel ? (
        <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          {row.rateLabel ? <span>{row.typeLabel}</span> : null}
          {row.rateLabel && row.sourceLabel ? <span aria-hidden>·</span> : null}
          {row.sourceLabel ? <span>{row.sourceLabel}</span> : null}
        </span>
      ) : null}
    </div>
  )

  const renderActions = (
    member: CompanyMember,
    displayName: string
  ): React.ReactElement | null => {
    if (!canUpdateMember && !canRemoveMember) return null
    return (
      <TableRowActions>
        {canUpdateMember && (
          <TableRowAction
            icon={Pencil}
            label={`Edit ${displayName}`}
            onSelect={() => setMemberToEdit(member)}
          />
        )}
        {canRemoveMember && (
          <TableRowAction
            icon={UserMinus}
            label={`Remove ${displayName}`}
            variant="destructive"
            onSelect={() => setMemberToRemove(member)}
            disabled={removeMember.isPending || !!memberToRemove}
          />
        )}
      </TableRowActions>
    )
  }

  const renderTable = (): React.ReactElement => (
    <DataTable className="hidden md:block" tableClassName="w-full">
      <DataTableHead>
        <tr>
          <DataTableHeadCell className="w-[32%] px-6 py-3">
            Member
          </DataTableHeadCell>
          <DataTableHeadCell className="w-[18%] px-6 py-3">
            Company
          </DataTableHeadCell>
          <DataTableHeadCell className="w-[11%] px-6 py-3">
            Role
          </DataTableHeadCell>
          <DataTableHeadCell className="w-[20%] px-6 py-3">
            Compensation
          </DataTableHeadCell>
          <DataTableHeadCell className="w-[10%] px-6 py-3">
            Status
          </DataTableHeadCell>
          <DataTableHeadCell align="right" className="w-[9%] px-6 py-3">
            <span className="sr-only">Actions</span>
          </DataTableHeadCell>
        </tr>
      </DataTableHead>
      <DataTableBody>
        {members.map((member) => {
          const row = describeMember(member)
          return (
            <DataTableRow
              key={row.key}
              className={cn('group/row', canUpdateMember && 'cursor-pointer')}
              onClick={() => handleRowActivate(member)}
            >
              <DataTableCell noWrap={false} className="px-6 py-3 align-middle">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar name={row.displayName} size="md" tone="soft" />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900 dark:text-gray-100">
                      {row.displayName}
                    </p>
                    {row.email ? (
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {row.email}
                      </p>
                    ) : null}
                  </div>
                </div>
              </DataTableCell>

              <DataTableCell noWrap={false} className="px-6 py-3 align-middle">
                {renderCompany(row)}
              </DataTableCell>

              <DataTableCell className="px-6 py-3 align-middle">
                {renderRole(row)}
              </DataTableCell>

              <DataTableCell noWrap={false} className="px-6 py-3 align-middle">
                {renderCompensation(row)}
              </DataTableCell>

              <DataTableCell className="px-6 py-3 align-middle">
                <Badge variant={row.isActive ? 'success' : 'neutral'} dot>
                  {row.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </DataTableCell>

              <DataTableCell align="right" className="px-6 py-3 align-middle">
                {renderActions(member, row.displayName)}
              </DataTableCell>
            </DataTableRow>
          )
        })}
      </DataTableBody>
    </DataTable>
  )

  /**
   * Phone layout. A six-column table in a 390px viewport is a horizontal
   * scroller that hides status and actions entirely, so below `md` each member
   * becomes a card with the same facts stacked and the actions always visible.
   */
  const renderCards = (): React.ReactElement => (
    <ul className="flex flex-col gap-2 md:hidden">
      {members.map((member) => {
        const row = describeMember(member)
        return (
          <li
            key={row.key}
            className={cn(
              'group/row rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-800',
              canUpdateMember && 'cursor-pointer active:bg-gray-50 dark:active:bg-gray-700/40'
            )}
            onClick={() => handleRowActivate(member)}
          >
            <div className="flex items-start gap-3">
              <Avatar name={row.displayName} size="lg" tone="soft" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-900 dark:text-gray-100">
                  {row.displayName}
                </p>
                {row.email ? (
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {row.email}
                  </p>
                ) : null}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  {renderCompany(row)}
                  {renderRole(row)}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <Badge variant={row.isActive ? 'success' : 'neutral'} dot size="sm">
                  {row.isActive ? 'Active' : 'Inactive'}
                </Badge>
                {renderActions(member, row.displayName)}
              </div>
            </div>
            <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-700/60">
              {renderCompensation(row)}
            </div>
          </li>
        )
      })}
    </ul>
  )

  return (
    <>
      <PageShell
        breadcrumbItems={[
          { label: 'Workforce', to: '/dashboard' },
          { label: 'Members' },
        ]}
        icon={Users}
        title="Members"
        description="Manage who belongs to which company. Pick a single company to add new members."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={<WorkspaceCompanyChips filter={filter} />}
            actions={
              <PermissionGate module={COMPANY_MEMBERS_MODULE} action="add">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setAddModalOpen(true)}
                  disabled={!addCompanyId}
                  title={
                    addCompanyId
                      ? undefined
                      : 'Select exactly one company chip to add a member'
                  }
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span className="ml-1.5">Add member</span>
                </Button>
              </PermissionGate>
            }
          >
            <WorkspaceToolbarSearch
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search member or company…"
              ariaLabel="Search members"
              active={Boolean(search)}
            />
          </WorkspaceToolbar>

          {/* One filter line, no card. Three-option filters are segmented
              controls: every choice is already on screen, so narrowing the
              list costs one click instead of open-then-pick. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Status
              </span>
              <SegmentedControl
                ariaLabel="Filter by status"
                size="sm"
                options={[...STATUS_FILTER_OPTIONS]}
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value as StatusFilter)
                  setPage(1)
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Pay
              </span>
              <SegmentedControl
                ariaLabel="Filter by compensation type"
                size="sm"
                options={[...COMPENSATION_FILTER_OPTIONS]}
                value={compensationFilter}
                onChange={(value) => {
                  setCompensationFilter(value as CompensationFilter)
                  setPage(1)
                }}
              />
            </div>

            {hasActiveFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-gray-600 dark:text-gray-400"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                <span className="ml-1">Reset</span>
              </Button>
            ) : null}

            <p
              className="ml-auto text-xs text-gray-500 dark:text-gray-400"
              aria-live="polite"
            >
              {isLoading
                ? 'Loading members…'
                : `${total} ${total === 1 ? 'member' : 'members'}`}
            </p>
          </div>

          {error ? (
            <QueryErrorState
              error={error}
              title="Couldn't load members"
              onRetry={refetch}
            />
          ) : isLoading ? (
            <DataTableCard>
              <TableSkeleton rows={limit > 10 ? 10 : limit} cols={6} />
            </DataTableCard>
          ) : members.length === 0 ? (
            <EmptyState
              icon={Users}
              title={
                hasActiveFilters ? 'No members match these filters' : 'No members yet'
              }
              description={
                hasActiveFilters
                  ? 'Try a different company, status, or search term.'
                  : 'Pick a single company above, then add its first member.'
              }
              action={
                hasActiveFilters ? (
                  <Button variant="secondary" size="sm" onClick={handleResetFilters}>
                    Reset filters
                  </Button>
                ) : null
              }
            />
          ) : (
            <>
              {renderCards()}
              {renderTable()}

              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onLimitChange={setLimit}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                className="px-6 py-3"
              />
            </>
          )}
        </div>
      </PageShell>

      {addCompanyId && (
        <AddCompanyMemberModal
          companyId={addCompanyId}
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={() => {}}
          existingMembers={existingMembersForAdd}
        />
      )}

      <EditCompanyMemberModal
        companyId={memberToEdit ? getMemberCompanyId(memberToEdit) : ''}
        companyName={
          memberToEdit
            ? (companyById.get(getMemberCompanyId(memberToEdit))?.name ?? null)
            : null
        }
        member={memberToEdit}
        open={memberToEdit !== null}
        onClose={() => setMemberToEdit(null)}
        onSuccess={() => setMemberToEdit(null)}
      />

      <RemoveCompanyMemberModal
        open={memberToRemove !== null}
        memberName={memberToRemove ? getMemberDisplayName(memberToRemove) : ''}
        companyName={
          (memberToRemove &&
            companyById.get(getMemberCompanyId(memberToRemove))?.name) ||
          'this company'
        }
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleConfirmRemove}
        loading={removeMember.isPending}
      />
    </>
  )
}

WorkspaceMembersPage.displayName = 'WorkspaceMembersPage'
